import { useState } from "react";
import { Modal, Table, Form, Button } from "react-bootstrap";
import { useQuery } from "react-query";
import ReactPaginate from "react-paginate";
import currency from "currency.js";
import { toast } from "react-toastify";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import queryString from "query-string";

import useDebounce, { useBackendUrl } from "../../../utils/hooks";
import {
  formatDate,
  formatForQty,
  paginationOptions,
} from "../../../utils/helpers";

export default function DamageBatchPicker({ show, onHide, onAdd }) {
  const backendUrl = useBackendUrl();

  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 20,
    q: "",
    model: "StockBatches",
    getItemFrom: "StockBatches",
    withQuantity: true,
    withExpiry: true,
  });

  const [qtyByBarcode, setQtyByBarcode] = useState({});

  const debouncedQueryParams = useDebounce(queryParams, 500);

  const getBatchItems = async debouncedQueryParams => {
    let response = await fetch(
      `${backendUrl}/api/items?${queryString.stringify(debouncedQueryParams)}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    return data;
  };

  const { data = { items: [], count: 0 }, isFetching } = useQuery(
    ["DAMAGE_BATCH_PICKER", debouncedQueryParams],
    () => getBatchItems(debouncedQueryParams),
    {
      enabled: show,
      keepPreviousData: true,
      onError: () => toast.error("Unable to load batches, try again"),
    }
  );

  const handleAdd = batch => {
    const qty = Number(qtyByBarcode[batch.Bar_Code] || 0);

    if (qty <= 0) {
      toast.error("Enter a quantity to request");
      return;
    }

    if (qty > Number(batch.Quantity)) {
      toast.error(`Qty cannot exceed available quantity (${batch.Quantity})`);
      return;
    }

    onAdd({
      batchBarCode: batch.Bar_Code,
      itemName: batch.ItemName,
      batchName: batch.Batch_Name,
      availableQuantity: batch.Quantity,
      expireDate: batch.ExpireDate,
      unitCost: batch.UnitCost,
      atcCode: batch.ATC_Code,
      quantity: qty,
    });

    setQtyByBarcode(prev => ({ ...prev, [batch.Bar_Code]: "" }));
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      animation={false}
      enforceFocus={false}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className="h6 mb-0">Select Item From Batch</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-3 align-items-center mb-3">
          <MagnifyIcon />
          <Form.Control
            placeholder="Search batch or item..."
            value={queryParams.q}
            onChange={e =>
              setQueryParams(p => ({ ...p, page: 1, q: e.target.value }))
            }
            autoFocus
          />

          <Button variant="primary" onClick={onHide}>
            Done
          </Button>
        </div>

        <Table borderless hover striped className="product-table">
          <thead className="sticky text-nowrap">
            <tr>
              <th scope="col">Batch</th>
              <th scope="col">Item Name</th>
              <th scope="col">ATC Code</th>
              <th scope="col">Unit Cost</th>
              <th scope="col">Available Qty</th>
              <th scope="col">Expiry Date</th>
              <th scope="col">Qty to Damage</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isFetching ? (
              <tr>
                <td colSpan={8} className="text-center py-3">
                  Loading…
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-3">
                  No batches found
                </td>
              </tr>
            ) : (
              data.items.map((el, index) => (
                <tr key={index}>
                  <td>
                    {el.Bar_Code ? el.Bar_Code.split("-").pop() : "..."}{" "}
                    {el.Batch_Name ? <i>({el.Batch_Name})</i> : null}
                  </td>
                  <td>{el.ItemName}</td>
                  <td>{el.ATC_Code || "..."}</td>
                  <td>
                    {currency(el.UnitCost, {
                      precision: 2,
                      symbol: "",
                    }).format()}
                  </td>
                  <td>
                    {currency(el.Quantity, {
                      precision: 2,
                      format: formatForQty,
                      symbol: "",
                    }).format()}
                  </td>
                  <td className="text-nowrap">
                    {el.ExpireDate
                      ? formatDate(el.ExpireDate, "dd MMM, yyyy")
                      : "..."}
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min={0}
                      max={el.Quantity}
                      placeholder="0"
                      style={{ width: "100px" }}
                      value={qtyByBarcode[el.Bar_Code] || ""}
                      onChange={e =>
                        setQtyByBarcode(prev => ({
                          ...prev,
                          [el.Bar_Code]: e.target.value,
                        }))
                      }
                    />
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleAdd(el)}
                    >
                      Add
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-items-between w-100 gap-3">
          <div>
            {data.count > queryParams.limit && (
              <ReactPaginate
                {...paginationOptions}
                pageCount={Math.ceil(data.count / queryParams.limit)}
                forcePage={queryParams.page - 1}
                onPageChange={({ selected }) =>
                  setQueryParams(p => ({ ...p, page: selected + 1 }))
                }
              />
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
